import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AdminAuthProvider } from './hooks/useAdminAuth';
import { EnterpriseAdminLayout } from './components/layout/EnterpriseAdminLayout';
import { LoginPage } from './pages/auth/LoginPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { UsersPage } from './pages/users/UsersPage';
import { UserDetailPage } from './pages/users/UserDetailPage';
import { WorkflowsPage } from './pages/workflows/WorkflowsPage';
import { WorkflowHealthPage } from './pages/workflows/WorkflowHealthPage';
import { IntegrationsPage } from './pages/integrations/IntegrationsPage';
import { SystemPage } from './pages/system/SystemPage';
import { MonitoringPage } from './pages/monitoring/MonitoringPage';
import { FastAPIMonitoringPage } from './pages/monitoring/FastAPIMonitoringPage';
import { CopilotMetricsPage } from './pages/monitoring/CopilotMetricsPage';
import { SupabaseMonitorPage } from './pages/monitoring/SupabaseMonitorPage';
import { InvoicesPage } from './pages/invoices/InvoicesPage';
import { TdsFollowupPage } from './pages/invoices/TdsFollowupPage';
import { PricingPage } from './pages/pricing/PricingPage';
import { PhoneNumbersPage } from './pages/phone-numbers/PhoneNumbersPage';
import { CallingProvidersPage } from './pages/calling-providers/CallingProvidersPage';
import { ApiKeysPage } from './pages/api-keys/ApiKeysPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { EnterpriseWorkspacesPage } from './pages/enterprise/EnterpriseWorkspacesPage';
import { EnterpriseWorkspaceDetailPage } from './pages/enterprise/EnterpriseWorkspaceDetailPage';
import { EnterpriseVerifiedDomainsPage } from './pages/enterprise/EnterpriseVerifiedDomainsPage';
import { EnterpriseAuditPage } from './pages/enterprise/EnterpriseAuditPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { InvoiceTemplatesPage } from './pages/settings/InvoiceTemplatesPage';
import { NotificationSendersPage } from './pages/settings/NotificationSendersPage';
import { FirecrawlConfigPage } from './pages/settings/FirecrawlConfigPage';
import { SearxngConfigPage } from './pages/settings/SearxngConfigPage';
import { JarvisToolsPage } from './pages/jarvis/JarvisToolsPage';
import LatencyProfilerPage from './pages/latency-profiler/LatencyProfilerPage';
import { SubscriptionsPage } from './pages/subscriptions/SubscriptionsPage';
import { OperationsCenterLayout } from './pages/operations/OperationsCenterLayout';
import { VoiceInfrastructureLayout } from './pages/voice-infrastructure/VoiceInfrastructureLayout';
import { VoicePlatformsPage } from './pages/voice-infrastructure/VoicePlatformsPage';
import {
  VoiceInfraOverviewPage,
  VoiceInfraProvidersPage,
  VoiceInfraTrunksPage,
  VoiceInfraPhonesPage,
  VoiceInfraCarriersPage,
  VoiceInfraGeoRoutesPage,
  VoiceInfraHealthPage,
  VoiceInfraFailoverPage,
  VoiceInfraDiagnosticsPage,
  VoiceInfraProvisioningPage,
} from './pages/voice-infrastructure/VoiceInfraPages';
import { OperationsDashboardPage } from './pages/operations/OperationsDashboardPage';
import { OperationsWorkerPlanePage } from './pages/operations/OperationsWorkerPlanePage';
import { OperationsQueuesPage } from './pages/operations/OperationsQueuesPage';
import { OperationsPoliciesPage } from './pages/operations/OperationsPoliciesPage';
import { OperationsRuntimePage } from './pages/operations/OperationsRuntimePage';
import { OperationsHistoryPage } from './pages/operations/OperationsHistoryPage';
import { OperationsMigrationPage } from './pages/operations/OperationsMigrationPage';
import { PlatformOperationsLayout } from './pages/platform-operations/PlatformOperationsLayout';
import { PlatformOpsScreenPage } from './pages/platform-operations/PlatformOpsScreenPage';
import { PLATFORM_OPS_SCREENS } from './pages/platform-operations/platformOpsNav';

function AdminRootOutlet() {
  return <Outlet />;
}

/** Maps retired `/admin/app/*` bookmarks onto the unified `/admin/*` tree. */
function LegacyEnterpriseAppRedirect() {
  const { pathname, search, hash } = useLocation();
  const rest = pathname.replace(/^\/admin\/app\/?/, '');
  let suffix = rest || 'dashboard';
  if (suffix === 'workspaces/table') suffix = 'workspaces';
  return <Navigate to={`/admin/${suffix}${search}${hash}`} replace />;
}

function App() {
  return (
    <AdminAuthProvider>
      <Router>
        <Routes>
          <Route path="/admin/login" element={<LoginPage />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminRootOutlet />
              </ProtectedRoute>
            }
          >
            <Route path="app/*" element={<LegacyEnterpriseAppRedirect />} />
            <Route path="app" element={<Navigate to="/admin/dashboard" replace />} />

            <Route element={<EnterpriseAdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="users/:userId" element={<UserDetailPage />} />
              <Route path="workspaces/table" element={<Navigate to="/admin/workspaces" replace />} />
              <Route path="workspaces/:workspaceId" element={<EnterpriseWorkspaceDetailPage />} />
              <Route path="workspaces" element={<EnterpriseWorkspacesPage />} />
              <Route path="verified-domains" element={<EnterpriseVerifiedDomainsPage />} />
              <Route path="audit" element={<EnterpriseAuditPage />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="tds-followup" element={<TdsFollowupPage />} />
              <Route path="subscriptions" element={<SubscriptionsPage />} />
              <Route path="phone-numbers" element={<PhoneNumbersPage />} />
              <Route path="calling-providers" element={<CallingProvidersPage />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="workflows" element={<WorkflowsPage />} />
              <Route path="workflow-health" element={<WorkflowHealthPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/invoice-templates" element={<InvoiceTemplatesPage />} />
              <Route path="settings/notification-senders" element={<NotificationSendersPage />} />
              <Route path="settings/firecrawl" element={<FirecrawlConfigPage />} />
              <Route path="settings/searxng" element={<SearxngConfigPage />} />
              <Route path="system" element={<SystemPage />} />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="fastapi-monitoring" element={<FastAPIMonitoringPage />} />
              <Route path="copilot-metrics" element={<CopilotMetricsPage />} />
              <Route path="jarvis-tools" element={<JarvisToolsPage />} />
              <Route path="latency-profiler" element={<LatencyProfilerPage />} />
              <Route path="supabase-monitor" element={<SupabaseMonitorPage />} />
              <Route path="operations" element={<OperationsCenterLayout />}>
                <Route index element={<OperationsDashboardPage />} />
                <Route path="workers" element={<OperationsWorkerPlanePage />} />
                <Route path="queues" element={<OperationsQueuesPage />} />
                <Route path="policies" element={<OperationsPoliciesPage />} />
                <Route path="runtime" element={<OperationsRuntimePage />} />
                <Route path="history" element={<OperationsHistoryPage />} />
                <Route path="migration" element={<OperationsMigrationPage />} />
              </Route>
              <Route path="voice-infrastructure" element={<VoiceInfrastructureLayout />}>
                <Route index element={<VoiceInfraOverviewPage />} />
                <Route path="voice-platforms" element={<VoicePlatformsPage />} />
                <Route path="providers" element={<VoiceInfraProvidersPage />} />
                <Route path="sip-trunks" element={<VoiceInfraTrunksPage />} />
                <Route path="phone-numbers" element={<VoiceInfraPhonesPage />} />
                <Route path="carriers" element={<VoiceInfraCarriersPage />} />
                <Route path="geo-routes" element={<VoiceInfraGeoRoutesPage />} />
                <Route path="health" element={<VoiceInfraHealthPage />} />
                <Route path="failover" element={<VoiceInfraFailoverPage />} />
                <Route path="diagnostics" element={<VoiceInfraDiagnosticsPage />} />
                <Route path="provisioning" element={<VoiceInfraProvisioningPage />} />
              </Route>
              <Route path="platform-operations" element={<PlatformOperationsLayout />}>
                <Route index element={<PlatformOpsScreenPage />} />
                {PLATFORM_OPS_SCREENS.filter((s) => s.id !== 'runtime-dashboard').map((s) => {
                  const slug = s.path.split('/').pop()!;
                  return <Route key={s.id} path={slug} element={<PlatformOpsScreenPage />} />;
                })}
              </Route>
            </Route>
          </Route>

          <Route path="/" element={<Navigate to="/admin/login" replace />} />
          <Route path="*" element={<Navigate to="/admin/login" replace />} />
        </Routes>
      </Router>
    </AdminAuthProvider>
  );
}

export default App;
